import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useCreateSettlementMutation } from '../services/ledger/ledgerApi';

const CloseIcon = () => (
  <svg viewBox="64 64 896 896" focusable="false" width="1em" height="1em" fill="currentColor">
    <path d="M563.8 512l262.5-312.9c4.4-5.2.7-13.1-6.1-13.1h-79.8c-4.7 0-9.2 2.1-12.3 5.7L511.6 449.8 295.1 191.7c-3-3.6-7.5-5.7-12.3-5.7H203c-6.8 0-10.5 7.9-6.1 13.1L459.4 512 196.9 824.9A7.95 7.95 0 00203 838h79.8c4.7 0 9.2-2.1 12.3-5.7l216.5-258.1 216.5 258.1c3 3.6 7.5 5.7 12.3 5.7h79.8c6.8 0 10.5-7.9 6.1-13.1L563.8 512z" />
  </svg>
);

const LedgerSettlementModal = ({ isOpen, onClose, user, type, onSuccess }) => {
  const [settleAmount, setSettleAmount] = useState('');
  const [remark, setRemark] = useState('');
  const [createSettlement, { isLoading }] = useCreateSettlementMutation();

  useEffect(() => {
    if (isOpen) {
      setSettleAmount('');
      setRemark('');
    }
  }, [isOpen, user]);

  if (!isOpen || !user) return null;

  const displayName = user.fullname ? `${user.fullname} (${user.username})` : user.username;

  const handleSubmit = async (e) => {
    e.preventDefault();

    const amount = parseFloat(settleAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid settle amount');
      return;
    }

    try {
      await createSettlement({
        userId: user.userId,
        amount,
        type,
        remark: remark.trim() || undefined,
      }).unwrap();

      toast.success('Settlement recorded successfully');
      onClose();
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to record settlement');
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
          {/* Header */}
          <div className="bg-[#0F2327] px-5 py-3 flex items-center justify-between rounded-t-lg">
            <h1 className="text-white text-lg font-normal">
              Settlement {displayName}
            </h1>
            <button onClick={onClose} className="text-gray-300 hover:text-white transition-colors" aria-label="Close">
              <CloseIcon />
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Username</label>
              <input
                type="text"
                value={user.username}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-50 text-gray-500 cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">Amount</label>
              <input
                type="text"
                value={Number(user.balance || 0).toFixed(2)}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-50 text-gray-500 cursor-not-allowed"
              />
            </div>

            <span
              className={`inline-block px-3 py-1 rounded text-xs font-bold ${
                type === 'DENA' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
              }`}
            >
              {type}
            </span>

            <div>
              <label className="block text-sm text-gray-600 mb-1">* Settle Amount</label>
              <input
                type="number"
                step="0.01"
                min="0"
                placeholder="Enter amount"
                value={settleAmount}
                onChange={(e) => setSettleAmount(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">Remark</label>
              <textarea
                placeholder="Enter any remarks"
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors disabled:opacity-50"
              >
                {isLoading ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LedgerSettlementModal;
